import React, { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { ExternalLink, Github, ArrowRight, Layers, Cpu, Database, BookOpen, CheckCircle2 } from "lucide-react";
import { PROJECTS } from "../data/mock";
import { CASE_STUDIES } from "../data/caseStudies";
import SectionHeader from "../components/common/SectionHeader";
import FeaturedProjectCard from "../components/portfolio/FeaturedProjectCard";
import SupportingProjectCard from "../components/portfolio/SupportingProjectCard";

export default function Works() {
  const [filter, setFilter] = useState("All");

  const disciplines = ["All", "Frontend Architecture", "Full-Stack Systems", "Applied AI / LLM", "Research Pipelines"];

  const filteredProjects = useMemo(() => {
    if (filter === "All") return PROJECTS;
    return PROJECTS.filter((p) => p.capability === filter);
  }, [filter]);

  return (
    <section className="pt-36 sm:pt-40 pb-28 px-6 sm:px-8 lg:px-12">
      <div className="max-w-[1360px] mx-auto">
        <SectionHeader
          eyebrow="Systems & Architecture"
          title="Selected"
          highlight="platforms."
          meta={`${PROJECTS.length} verified projects`}
        />

        <p className="text-foreground/75 text-lg sm:text-xl max-w-[65ch] -mt-6 mb-10 font-normal leading-relaxed">
          Production systems, applied retrieval architectures, and empirical informatics tools. Each project represents an observable problem space with verified source code or active deployment.
        </p>

        {/* Discipline Filters */}
        <div className="flex flex-wrap items-center gap-2 mb-12 pb-6 border-b border-border/60">
          {disciplines.map((d) => (
            <button
              key={d}
              onClick={() => setFilter(d)}
              className={`font-mono text-[11px] tracking-[0.18em] uppercase px-4 py-2 rounded-full border transition-all ${
                filter === d
                  ? "border-primary/80 text-primary bg-primary/10 shadow-[0_0_15px_hsl(var(--accent)/0.15)]"
                  : "border-border/70 text-muted-foreground hover:text-foreground hover:border-border"
              }`}
            >
              {d}
            </button>
          ))}
        </div>

        {/* Featured Dominant Project Showcase (when showing All or Frontend Architecture) */}
        {(filter === "All" || filter === "Frontend Architecture") && (
          <div className="mb-12">
            <FeaturedProjectCard project={PROJECTS[0]} />
          </div>
        )}

        {/* Supporting Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-20">
          {(filter === "All" ? PROJECTS.slice(1) : filteredProjects.filter((p) => p.id !== "01")).map((p, idx) => (
            <SupportingProjectCard key={p.id} project={p} index={idx} />
          ))}
        </div>

        {/* Technical Architecture Comparison Matrix */}
        <div className="rounded-2xl border border-border/80 bg-card/40 p-6 sm:p-8">
          <div className="mb-6">
            <span className="font-mono text-[11px] tracking-[0.2em] uppercase text-primary font-medium block">
              Architectural Specifications Matrix
            </span>
            <p className="font-mono text-[11px] text-muted-foreground mt-0.5">
              Comparative overview of operational scopes, deployment targets, and evidence status
            </p>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left font-mono text-[12px] border-collapse">
              <thead>
                <tr className="border-b border-border/70 text-muted-foreground text-[10.5px] uppercase tracking-wider">
                  <th className="py-3 px-3">Project</th>
                  <th className="py-3 px-3">Discipline</th>
                  <th className="py-3 px-3">Core Stack</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3 text-right">Case Study</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border/40 text-foreground/85">
                {CASE_STUDIES.map((cs) => (
                  <tr key={cs.id} className="hover:bg-secondary/20 transition-colors">
                    <td className="py-3.5 px-3 font-medium text-foreground">
                      <Link to={`/works/${cs.slug}`} className="hover:text-primary transition-colors">
                        {cs.name}
                      </Link>
                    </td>
                    <td className="py-3.5 px-3 text-muted-foreground">{cs.type}</td>
                    <td className="py-3.5 px-3">
                      <span className="text-[11px] text-primary/90">{cs.stack.join(" · ")}</span>
                    </td>
                    <td className="py-3.5 px-3">
                      <span
                        className={`inline-flex items-center gap-1.5 px-2 py-0.5 rounded-full text-[10px] tracking-wider uppercase border ${
                          cs.status.includes("Active") || cs.status.includes("Production")
                            ? "border-emerald-500/30 text-emerald-400 bg-emerald-500/10"
                            : "border-border text-muted-foreground bg-secondary/30"
                        }`}
                      >
                        {cs.status}
                      </span>
                    </td>
                    <td className="py-3.5 px-3 text-right">
                      <Link
                        to={`/works/${cs.slug}`}
                        className="inline-flex items-center gap-1 text-[11px] tracking-wider uppercase text-primary hover:underline"
                      >
                        Details &rarr;
                      </Link>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

      </div>
    </section>
  );
}
